/* eslint-disable camelcase */
import cookie from 'js-cookie'
import * as Sentry from '@sentry/nuxt'
import pkceChallenge from 'pkce-challenge'
import { singleItemWrapper } from '@/api/api-client/wrappers'
import type { DeepPartial, Query } from '@/api/api-client/wrappers/types'
import { accessStorage } from '@/api/storage'
import { createMaybeDebugFn } from '@/api/debug'
import { getWidgetToken, setWidgetToken } from '~/utils/chat-widget'
import type { Starter, Options } from '@/api/modules/types/internal'
import type {
  IdentityField,
  Identity,
  PopupOptions,
  Client,
  AuthCredentials,
  Token,
  AuthInstructions,
  Login,
  LoginSocialCompleteResult,
  AccountExpanded
} from './types'
import { loadEnvironmentConfig } from '@/configs/environments/config'
import { AUTH_ERRORS_CODES } from '@/consts/errors'
import { analytics } from '~/_plugins/analytics/core'

type FetchedAccount = DeepPartial<AccountExpanded>
type UrlFragmentation = Record<string, string>

interface AuthOptions extends Options {
  callbackPath: string
  navigateTo: (path: string) => any
  normalizePath?: (path: string) => string
  onLoginSocialComplete?: (social: Login['social']) => any
  onPopupAuthOpen?: (clientId: Client['id']) => any
  onPopupAuthClose?: (clientId: Client['id']) => any
  onPopupAuthError: (error: string) => void
  onFetchAccount?: (account: FetchedAccount | null) => any
  accountRequestQuery?: Query
}

const POPUP_AUTH_ERROR_MESSAGE = 'Popup auth: Unauthorized'
const POPUP_AUTH_ERROR_SNACK_TEXT =
  'Не удалось войти. Пожалуйста, попробуйте еще раз'
const POPUP_WAIT_MESSAGE_INTERVAL = 1000
const POPUP_WAIT_URL_INTERVAL = 1000
const IFRAME_POSTLOAD_DELAY = 1000
const EXPECTED_AUTH_ERROR_CODES = [AUTH_ERRORS_CODES.InvalidCredentials]

export default {
  optionsRequired: true,

  defaultOptions: {
    normalizePath: (path: string) => path
  },

  factory: ({
    apiClient,
    version,
    debug,
    callbackPath,
    navigateTo,
    normalizePath,
    onLoginSocialComplete,
    onPopupAuthOpen,
    onPopupAuthClose,
    onPopupAuthError,
    onFetchAccount,
    accountRequestQuery
  }) => {
    const maybeDebug = createMaybeDebugFn(debug)

    if (!normalizePath) {
      throw new ReferenceError(
        'normalizePath must be specified in defaultOptions'
      )
    }

    const storage = accessStorage<{
      pass_token: Token | null
      login: Login | null
      referrer: number | string | null
    }>('dphub/auth', {
      pass_token: null,
      login: null,
      referrer: null
    })

    const requests: {
      account: Promise<FetchedAccount> | null
      clients: Promise<Client[]> | null
      token: Promise<Token | null> | null
    } = {
      account: null,
      clients: null,
      token: null
    }

    const data: {
      account: FetchedAccount | null
      clients: Client[]
    } = {
      account: null,
      clients: []
    }

    let hasTriedAuthByIdentity = false
    const pkceChallengeGeneration = pkceChallenge()

    const setToken = (token: Token | null = null) => {
      if (token) {
        token.expires_in =
          Math.floor(Date.now() / 1000) + token.expiration_time - 10
      }

      storage.setItem('pass_token', token)
      writeLog({ text: `SET TOKEN`, type: 'success', info: token })

      maybeDebug()
    }

    const getRedirectUri = () => location.origin + callbackPath

    const getIdentityFieldValue = (
      identityType: Identity['type'],
      field: IdentityField
    ) => {
      if (identityType === 'cookie') {
        return cookie.get(field.get)
      }

      if (identityType === 'window') {
        return field.get
          .split('.')
          .reduce((acc: any, part) => acc?.[part], window)
      }

      throw new Error(
        `Identity type ${identityType} does not support getting field values`
      )
    }

    const writeLog = ({
      text,
      type,
      info
    }: {
      text: string
      info?: any
      type?: 'success' | 'error' | 'info'
    }) => {
      if (type === 'success') {
        // eslint-disable-next-line no-console
        console.info(
          '%c Auth ',
          'color: white; background-color: #95B46A',
          text,
          info || ''
        )
      } else if (type === 'error') {
        // eslint-disable-next-line no-console
        console.info(
          '%c Auth ',
          'color: white; background-color: #D33F49',
          text,
          info || ''
        )
      } else {
        // eslint-disable-next-line no-console
        console.info(
          '%c Auth ',
          'color: white; background-color: #2274A5',
          text,
          info || ''
        )
      }
    }

    const isPopupClosed = (popup: Window | HTMLElement) =>
      popup instanceof HTMLElement ? !document.contains(popup) : popup.closed

    const popupWaitPostMessage = (popup: Window | HTMLElement) =>
      new Promise<AuthCredentials>((resolve, reject) => {
        writeLog({ text: 'OPEN IFRAME AND WAIT POST MESSAGE' })
        const onMessage = (e: MessageEvent) => {
          if (e.origin === 'https://www.ligastavok.ru') {
            writeLog({
              text: `GET POST MESSAGE FROM LIGA`,
              info: e.data,
              type: 'info'
            })
          }

          if (e.data?.payload?.error) {
            writeLog({
              text: 'ERROR IN POST MESSAGE',
              info: e.data.type,
              type: 'error'
            })

            const isExpectedError = EXPECTED_AUTH_ERROR_CODES.includes(
              e.data.payload.error.code
            )

            if (!isExpectedError) {
              onPopupAuthError(POPUP_AUTH_ERROR_SNACK_TEXT)

              Sentry.captureEvent({
                message: 'AUTH ERROR IN POST MESSAGE',
                level: 'error',
                tags: {
                  action: 'auth_iframe'
                },
                extra: {
                  e,
                  errorMessage: e.data.payload.error.message,
                  errorCode: e.data.payload.error.code
                }
              })
              stop()
              reject(new Error(POPUP_AUTH_ERROR_MESSAGE))
              return
            }
          }
          if (isPopupClosed(popup)) {
            console.warn('Popup close')
            writeLog({ text: `IFRAME CLOSE` })
            stop()
            reject(new Error(POPUP_AUTH_ERROR_MESSAGE))
          } else if (e.data?.payload?.ligaPassToken) {
            stop()
            const credentials = {
              ligaToken: e.data.payload.ligaPassToken,
              cascanaToken: e.data.payload.cascanaToken
            }
            resolve(credentials)
          }
        }

        const stop = () => {
          window.removeEventListener('message', onMessage)
        }

        window.addEventListener('message', onMessage)
      })

    const splitFragmentString = (fragmentString = '?') =>
      fragmentString
        .replace(/^[#?]/, '')
        .split('&')
        .reduce((res: UrlFragmentation, item) => {
          const [key, value] = item.split('=')

          if (key) {
            res[key] = value && decodeURIComponent(value)
          }

          return res
        }, {})

    const popupWaitUrl = (
      popupWindow: Window,
      params: {
        key: string
        type: 'hash' | 'search'
      }[]
    ) =>
      new Promise<UrlFragmentation>((resolve, reject) => {
        const interval = setInterval(() => {
          if (popupWindow.closed) {
            stop()
            reject(new Error(POPUP_AUTH_ERROR_MESSAGE))
          } else {
            let url: URL

            try {
              if (popupWindow.location.origin === location.origin) {
                url = new URL(popupWindow.location.href)
              } else {
                return
              }
            } catch {
              return
            }

            const hashParsed = splitFragmentString(url.hash)
            const searchParsed = splitFragmentString(url.search)

            const result = params.reduce(
              (acc: UrlFragmentation, { key, type }) => {
                switch (type) {
                  case 'hash':
                    acc[key] = hashParsed[key]
                    break

                  case 'search':
                    acc[key] = searchParsed[key]
                    break
                }

                return acc
              },
              {}
            )

            stop()
            resolve(result)
          }
        }, POPUP_WAIT_URL_INTERVAL)

        const stop = () => {
          clearInterval(interval)
        }
      })

    const getTokenBySocial = async (
      social: Client['id'],
      query: Record<string, any> = {},
      body: Record<string, any>,
      useAuthorization = true
    ) => {
      const referrer = storage.getItem('referrer')

      query.authclient = social
      query.redirect_uri = getRedirectUri()

      if (referrer) {
        writeLog({ text: `SET REFERRER_ID`, type: 'success' })
        query.referrer_id = referrer
      }

      const token = await apiClient.post<Token | AuthInstructions | null>(
        `/api/v${version}/auth`,
        {
          query,
          body
        },
        { useAuthorization }
      )
      writeLog({ text: `GET TOKEN API`, type: 'success' })

      maybeDebug()

      if (token && 'access_token' in token) {
        setToken(token)
        writeLog({ text: `SET TOKEN`, type: 'success', info: token })
      }

      return token
    }

    const devLogin = async (userNumber: number) => {
      const token = await apiClient.post<Token | AuthInstructions | null>(
        `/api/v${version}/auth`,
        {
          query: {
            redirect_uri: getRedirectUri(),
            authclient: 'liga'
          },
          body: {
            userNumber
          }
        },
        { useAuthorization: true }
      )

      maybeDebug()

      if (token && 'access_token' in token) {
        setToken(token)
      }

      return token
    }

    const loginSocialComplete = async (
      params: Record<string, any> | null = null,
      body: Record<string, any>
    ): Promise<LoginSocialCompleteResult> => {
      const login = storage.getItem('login')

      if (!login) {
        return { success: false }
      }

      storage.setItem('login', null)

      params ??= splitFragmentString(location.search)
      params.code_verifier = login.code_verifier

      try {
        let token: Token | AuthInstructions | null | undefined

        if (login.canSignup) {
          token = await getToken()
        }

        maybeDebug()

        if (!token) {
          token = await getTokenBySocial(login.social, params, body)
        }

        maybeDebug()

        if (token && 'access_token' in token) {
          await onLoginSocialComplete?.(login.social)

          return { success: true, redirect: login.redirect }
        }
      } catch {
        // ignore error
      }

      return { success: false }
    }

    const buildUrl = (url: string) => {
      const result = new URL(url)
      const local = new URL(location.href)

      local.searchParams.forEach((value, key) => {
        result.searchParams.set(key, value)
      })

      if (!result.searchParams.has('redirect_uri')) {
        result.searchParams.append('redirect_uri', getRedirectUri())
      }

      if (!result.searchParams.has('app')) {
        result.searchParams.append('app', 'ligapass')
      }

      return result.href
    }

    const openWindow = (url: string, popup?: Partial<PopupOptions> | null) => {
      const popupUrl = buildUrl(url)
      const features: string[] = []

      const popupOptions = Object.assign(
        {
          target: '_blank',
          features: '',
          height: 0,
          width: 0
        },
        popup
      )

      if (popupOptions.height && popupOptions.width) {
        const size = `height=${popupOptions.height},width=${popupOptions.width}`
        const position = `left=${
          window.innerWidth / 2 - popupOptions.width / 2
        },top=${window.innerHeight / 2 - popupOptions.height / 2}`

        features.push(size, position)
      }

      if (popupOptions.features) {
        features.push(popupOptions.features)
      }

      return window.open(popupUrl, popupOptions.target, features.join(','))
    }

    const openIframe = (url: string) => {
      const container = document.createElement('div')
      container.classList.add('iframe-auth')

      const closener = document.createElement('button')
      closener.innerHTML = '&times;'

      const iframe = document.createElement('iframe')
      iframe.src = buildUrl(url)

      Object.assign(iframe.style, {
        border: 'none',
        width: '100%',
        height: '100%'
      })

      iframe.onload = () => {
        setTimeout(() => {
          container.appendChild(closener)
        }, IFRAME_POSTLOAD_DELAY)
      }

      container.appendChild(iframe)
      document.body.appendChild(container)

      return {
        container,
        closener
      }
    }

    const devPopupAuth = async () => {
      const userNumber = await new Promise<number>((resolve, reject) => {
        const overlay = document.createElement('div')
        Object.assign(overlay.style, {
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: '10000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        })

        const popup = document.createElement('div')
        Object.assign(popup.style, {
          backgroundColor: 'rgba(70, 80, 98, 1)',
          borderRadius: '8px',
          padding: '20px',
          minWidth: '300px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        })

        const title = document.createElement('h3')
        title.textContent = 'Dev Login'
        title.style.marginBottom = '15px'
        title.style.textAlign = 'center'
        title.style.color = 'rgb(238, 239, 243)'
        title.style.margin = '0 0 15px 0'

        const input = document.createElement('input')
        Object.assign(input.style, {
          width: '100%',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          marginBottom: '15px',
          fontSize: '14px',
          boxSizing: 'border-box',
          backgroundColor: 'rgb(70, 80, 98)'
        })
        input.type = 'text'
        input.placeholder = 'Введите userNumber'
        input.value = '95211523' // значение по умолчанию

        const buttonContainer = document.createElement('div')
        buttonContainer.style.display = 'flex'
        buttonContainer.style.gap = '10px'

        const loginButton = document.createElement('button')
        loginButton.textContent = 'Войти'
        Object.assign(loginButton.style, {
          flex: '1',
          padding: '10px',
          backgroundColor: 'rgb(39, 219, 85)',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px'
        })

        const cancelButton = document.createElement('button')
        cancelButton.textContent = 'Отмена'
        Object.assign(cancelButton.style, {
          flex: '1',
          padding: '10px',
          backgroundColor: 'rgb(250, 55, 55)',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px'
        })

        // Обработчики событий
        const cleanup = () => {
          document.body.removeChild(overlay)
        }

        loginButton.onclick = () => {
          const value = parseInt(input.value, 10)
          if (isNaN(value) || value <= 0) {
            input.style.borderColor = 'red'
            return
          }
          cleanup()
          resolve(value)
        }

        cancelButton.onclick = () => {
          cleanup()
          reject(new Error('Cancelled'))
        }

        overlay.onclick = e => {
          if (e.target === overlay) {
            cleanup()
            reject(new Error('Cancelled'))
          }
        }

        input.onkeydown = e => {
          if (e.key === 'Enter') {
            loginButton.click()
          }
          // Сброс красной границы при вводе
          if (input.style.borderColor === 'red') {
            input.style.borderColor = '#ccc'
          }
        }

        input.oninput = () => {
          if (input.style.borderColor === 'red') {
            input.style.borderColor = '#ccc'
          }
        }

        popup.appendChild(title)
        popup.appendChild(input)
        buttonContainer.appendChild(loginButton)
        buttonContainer.appendChild(cancelButton)
        popup.appendChild(buttonContainer)
        overlay.appendChild(popup)
        document.body.appendChild(overlay)

        setTimeout(() => input.focus(), 100)
      })

      return await devLogin(userNumber)
    }

    const popupAuth = async ({ auth }: Client) => {
      return new Promise((resolve, reject) => {
        const popup = openIframe(auth.url)

        if (!popup.container) {
          reject(new Error(POPUP_AUTH_ERROR_MESSAGE))
        }

        popup.closener.onclick = () => {
          close()
          reject(new Error(POPUP_AUTH_ERROR_MESSAGE))
        }

        const close = async () => {
          writeLog({ text: `CLOSE IFRAME` })
          popup.container.remove()
        }

        popupWaitPostMessage(popup.container)
          .then((data: AuthCredentials) => {
            writeLog({
              text: `SUCCESS GET POST MESSAGE AND CONTINUE AUTH`,
              info: data,
              type: 'success'
            })

            if (!getWidgetToken()) {
              setWidgetToken(data?.cascanaToken)
            }
            close()
            resolve(loginSocialComplete(null, { token: data.ligaToken }))
          })
          .catch(async () => {
            writeLog({ text: `POST MESSAGE ERROR`, type: 'error' })
            await close()
            reject(new Error(POPUP_AUTH_ERROR_MESSAGE))
          })
      })
    }

    const refreshToken = async (token: Token) => {
      try {
        const newToken = await apiClient.post<Token>(
          `/api/v${version}/auth/refresh-token`,
          {
            body: {
              refresh_token: token.refresh_token
            }
          },
          { useAuthorization: false }
        )
        writeLog({ text: `REFRESH TOKEN`, type: 'success' })

        setToken(newToken)

        return newToken
      } catch {
        setToken(null)
        writeLog({ text: `REFRESH TOKEN ERROR`, type: 'error' })

        return null
      }
    }

    const fetchClients = async () => {
      if (!requests.clients) {
        const { code_challenge } = await pkceChallengeGeneration

        requests.clients = apiClient
          .get<Client[]>(
            `/api/v${version}/auth/clients`,
            {
              query: {
                redirect_uri: getRedirectUri(),
                code_challenge
              }
            },
            { useAuthorization: false }
          )
          .then(clients => {
            data.clients = clients

            return data.clients
          })
      }

      return requests.clients
    }

    const getClient = (id: Client['id']) =>
      data.clients.find(client => client.id === id)

    const getMainClient = () => data.clients.find(client => client.canSignup)

    const removeCookies = (client: Client) => {
      client.auth.identity?.fields.forEach(field => {
        cookie.remove(field.get)
        cookie.remove(field.get, {
          path: '/',
          domain: '.' + location.host.split('.').slice(-2).join('.')
        })
        cookie.remove(field.get, {
          path: '/',
          domain: '.' + location.host.replace(/^www\./, '')
        })
      })
    }

    const getToken = (update = true) => {
      if (!requests.token) {
        requests.token = (async () => {
          const token = storage.getItem('pass_token')

          if (token) {
            const expires = token.expires_in * 1000
            const expired = expires < Date.now()

            if (!expired) {
              return token
            }

            return update ? refreshToken(token) : null
          }

          if (!hasTriedAuthByIdentity && update) {
            hasTriedAuthByIdentity = true

            await fetchClients()

            return null
          }

          return null
        })()
      }

      return requests.token.then(result => {
        requests.token = null

        return result
      })
    }

    const getAuthorizationToken = async (update?: boolean) => {
      const token = await getToken(update)

      return token ? `Bearer ${token.access_token}` : null
    }

    const { get: getIdentity } = singleItemWrapper<AccountExpanded>(apiClient, {
      path: `/api/v${version}/auth/identity`
    })

    const getAccount = async (refresh = false) => {
      const token = await getToken()

      maybeDebug()

      if (!token) {
        return null
      }

      if (!requests.account || refresh) {
        requests.account = getIdentity<FetchedAccount>(accountRequestQuery)
          .then(account => {
            data.account = account

            return data.account
          })
          .then(async account => {
            await onFetchAccount?.(account)

            return account
          })
      }

      return requests.account
    }

    const popupWaitMessageSocial = (popup: Window, options?: any) =>
      new Promise<AuthCredentials>((resolve, reject) => {
        const interval = setInterval(() => {
          if (isPopupClosed(popup)) {
            stop()
            writeLog({
              text: 'CLOSE WINDOW TELEGRAM',
              type: 'error'
            })
            reject(new Error(POPUP_AUTH_ERROR_MESSAGE))
          }
        }, POPUP_WAIT_MESSAGE_INTERVAL)

        const onMessage = (e: MessageEvent) => {
          writeLog({
            text: 'GET TELEGRAM POST MESSAGE AUTH',
            type: 'success',
            info: e
          })
          let data

          try {
            data = JSON.parse(e.data)
          } catch {
            data = e.data
          }

          if (typeof data === 'object' && data.result) {
            writeLog({
              text: 'GET TELEGRAM POST MESSAGE AUTH WITH DATA RESULT',
              type: 'success',
              info: data
            })
            stop()
            resolve({
              ...data
            })
          }
        }

        const stop = () => {
          writeLog({
            text: 'STOP POST MESSAGE FROM TELEGRAM',
            type: 'info'
          })
          window.removeEventListener('message', onMessage)
          clearInterval(interval)
        }

        window.addEventListener('message', onMessage)
      })

    const loginBySocial = async ({ social, redirect }: Login) => {
      const clientData = getClient(social)

      if (!clientData) {
        throw new ReferenceError(`Client with id ${social} not found`)
      }

      const redirectPath = redirect
        ? normalizePath(redirect)
        : location.pathname

      const login: Login = {
        social,
        redirect: redirectPath,
        canSignup: clientData.canSignup
      }

      if (clientData.auth.pkce) {
        const { code_verifier } = await pkceChallengeGeneration
        login.code_verifier = code_verifier
      }

      storage.setItem('login', login)

      if (clientData.auth.popup) {
        const envConfig = await loadEnvironmentConfig()

        await onPopupAuthOpen?.(clientData.id)
        if (
          envConfig.ENVIRONMENT === 'prod' ||
          envConfig.ENVIRONMENT === 'ft'
        ) {
          await popupAuth(clientData)
        } else {
          await devPopupAuth()
        }
        await onPopupAuthClose?.(clientData.id)
        await getAccount(true)

        if (!(redirectPath && data.account)) {
          throw new Error('Unauthorized')
        }

        return navigateTo(redirectPath)
      }

      if (clientData.id === 'telegram') {
        const u = new URL(clientData.auth.url)
        // Telegram ожидает return_to, а не redirect_url
        u.searchParams.set('return_to', getRedirectUri())
        u.searchParams.delete('redirect_url')
        location.href = u.toString()
        return
      }

      location.href = clientData.auth.url
    }

    const isMobileDevice = () =>
      /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )

    if (!apiClient.hasAuthorization && typeof window !== 'undefined') {
      apiClient.setAuthorization(async ({ options }) => {
        const token = await getAuthorizationToken()

        // prettier-ignore
        if (token) {
          (options.headers as Record<string, string>).Authorization = token
        }
      })
    }

    return {
      popupWaitUrl,
      loginSocialComplete,
      openWindow,
      getAuthorizationToken,
      getIdentity,
      getAccount,
      fetchClients,

      /*
        Процесс открытия попапа должен быть синхронным,
        иначе сафари не откроет его.
      */
      login: (
        loginOptions: Partial<Login & { redirectIfAuthorized: boolean }> = {}
      ) => {
        const mainClient = getMainClient()

        if (!mainClient) {
          writeLog({ text: `ERROR GET MAIN CLIENT`, type: 'error' })
          throw new ReferenceError('Main client is not defined')
        }

        hasTriedAuthByIdentity = false

        const options = Object.assign(
          {
            social: mainClient.id,
            redirectIfAuthorized: true
          },
          loginOptions
        )

        if (options.redirectIfAuthorized && data.account && options.redirect) {
          return navigateTo(normalizePath(options.redirect))
        }

        return loginBySocial({
          social: options.social,
          redirect: options.redirect
        })
      },

      logout: async (reload = true) => {
        await fetchClients()
        const mainClient = getMainClient()

        try {
          if (mainClient?.auth.identity?.type === 'cookie') {
            removeCookies(mainClient)
          }

          const token = await getToken()

          if (token) {
            await apiClient.post(`/api/v${version}/auth/logout`, {
              body: {
                refresh_token: token.refresh_token
              }
            })
          }
          analytics('LigaPass_Logout_Success')
        } catch {
          // ignore error
        } finally {
          setToken(null)
          data.account = null
          await onFetchAccount?.(null)

          if (reload) {
            location.href = '/'
          }
        }
      },

      revoke: (social: Client['id']) =>
        apiClient.delete(`/api/v${version}/auth/revoke/${social}`),

      setReferrer: (id: number | string | null = null) =>
        storage.setItem('referrer', id),

      getClients: () => data.clients
    }
  }
} satisfies Starter<AuthOptions>
